import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Card,
  CardContent,
  Button,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormGroup,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { useAuth } from "../../context/authContext";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { url } from "../../constants/url";
import CustomTooltip from "../../components/CustomTooltip";

const ReportDetailView = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth() || {};

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [globalCategories, setGlobalCategories] = useState([]);
  const [selectedPathLevels, setSelectedPathLevels] = useState([]);

  // Download Modal State
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadType, setDownloadType] = useState("all");
  const [downloadCategories, setDownloadCategories] = useState(["ALL"]);
  const [isDownloading, setIsDownloading] = useState(false);

  const fetchReportDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [reportRes, catRes] = await Promise.all([
        axios.get(
          `${url}/api/field-inspection-reports/${reportId}${selectedCategory ? `?categoryName=${encodeURIComponent(selectedCategory)}` : ""}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(`${url}/api/issue-categories`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { success: false } })) // Fallback gracefully if API fails
      ]);

      if (reportRes.data.success) {
        setReport(reportRes.data.data);
      } else {
        setError(reportRes.data.message || "Failed to load report details");
      }

      if (catRes.data && catRes.data.success) {
        setGlobalCategories(catRes.data.data.map(c => c.name));
      }
    } catch (err) {
      console.error("Error fetching report details:", err);
      setError("Failed to load report details. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, [reportId, token, selectedCategory]);

  useEffect(() => {
    if (token && reportId) {
      fetchReportDetails();
    }
  }, [fetchReportDetails, token, reportId, selectedCategory]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Parse categories into a structured format for hierarchical filtering
  const parsedCategories = useMemo(() => {
    let baseCategories = [];
    
    // Helper to strip property type prefix from path levels
    const stripPropertyType = (levels) => {
      if (levels.length > 0) {
        const first = levels[0].toLowerCase();
        const isBhk = first.includes("bhk") || first === "studio" || first === "bungalow" || first.includes("shop") || first.includes("office");
        if (isBhk) {
          return levels.slice(1);
        }
      }
      return levels;
    };

    if (report?.categoryIssues) {
      baseCategories = report.categoryIssues.map((cat) => {
        if (cat.categoryName === "ALL") return { ...cat, baseCategory: "ALL", pathPart: "ALL", pathLevels: [] };
        
        let cleanName = cat.categoryName;
        const projectType = report?.projectId?.propertyType || report?.projectId?.projectType || "Residential";
        if (cleanName.toLowerCase().startsWith("residential - ")) {
           cleanName = cleanName.substring("residential - ".length).trim();
        } else if (projectType && cleanName.toLowerCase().startsWith(`${projectType.toLowerCase()} - `)) {
           cleanName = cleanName.substring(projectType.length + 3).trim();
        } else if (cleanName.toLowerCase() === "residential" || (projectType && cleanName.toLowerCase() === projectType.toLowerCase())) {
           cleanName = "General";
        }

        const parts = cleanName.split(" - ");
        const baseCategory = parts.length > 1 ? parts.pop().trim() : cleanName;
        let pathPart = parts.length > 0 ? parts.join(" - ").trim() : "General";
        if (parts.length === 0 || !pathPart) pathPart = "General";
        let pathLevels = pathPart.split("/").map((s) => s.trim()).filter(Boolean);
        pathLevels = stripPropertyType(pathLevels);
        pathPart = pathLevels.join(" / ");
        return { ...cat, categoryName: cleanName, baseCategory, pathPart, pathLevels };
      });
    }

    // Traverse fullProjectMappings to ensure all paths are represented
    if (report?.fullProjectMappings && Array.isArray(report.fullProjectMappings)) {
      const traverseMapping = (nodes, currentPathLevels) => {
        if (!nodes || nodes.length === 0) return;
        nodes.forEach(node => {
          const newPathLevels = [...currentPathLevels, node.title];
          const filteredPathLevels = stripPropertyType(newPathLevels);
          const pathPart = filteredPathLevels.join(" / ");
          
          // Check if this path exists in baseCategories
          const exists = baseCategories.some(c => c.pathPart === pathPart);
          if (!exists) {
            // Add a dummy "General" category for this empty path so it shows up in chips
            baseCategories.push({
              categoryName: `${pathPart} - General`,
              baseCategory: "General",
              pathPart,
              pathLevels: filteredPathLevels,
              yesCount: 0,
              noCount: 0,
              naCount: 0,
              criticalCount: 0,
              closedCount: 0,
              pendingCount: 0,
            });
          }
          if (node.subItems && node.subItems.length > 0) {
            traverseMapping(node.subItems, newPathLevels);
          }
        });
      };

      report.fullProjectMappings.forEach(mapping => {
        if (mapping.components) traverseMapping(mapping.components, []);
      });
    }

    return baseCategories;
  }, [report?.categoryIssues, report?.fullProjectMappings]);

  const maxDepth = useMemo(() => {
    return Math.max(0, ...parsedCategories.map((c) => c.pathLevels.length));
  }, [parsedCategories]);

  // Determine available dropdown options for each level
  const levelOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < maxDepth; i++) {
      const matchingCats = parsedCategories.filter((c) => {
        for (let j = 0; j < i; j++) {
          if (c.pathLevels[j] !== selectedPathLevels[j]) return false;
        }
        return c.pathLevels.length > i;
      });
      const uniqueAtLevel = [...new Set(matchingCats.map((c) => c.pathLevels[i]))].filter(Boolean);
      options.push(uniqueAtLevel);
    }
    return options;
  }, [parsedCategories, selectedPathLevels, maxDepth]);

  // Auto-select the first option for each level if nothing is selected
  useEffect(() => {
    if (parsedCategories.length === 0) return;
    const newSelection = [...selectedPathLevels];
    let changed = false;
    for (let i = 0; i < maxDepth; i++) {
      const options = levelOptions[i] || [];
      if (options.length > 0 && (!newSelection[i] || !options.includes(newSelection[i]))) {
        newSelection[i] = options[0];
        // Clear all deeper selections because the parent changed
        for (let j = i + 1; j < maxDepth; j++) newSelection[j] = undefined;
        changed = true;
      }
    }
    if (changed) {
      setSelectedPathLevels(newSelection.filter(Boolean));
    }
  }, [levelOptions, selectedPathLevels, maxDepth, parsedCategories]);

  // Determine which categories are available under the currently selected path
  const availableCategories = useMemo(() => {
    return parsedCategories.filter((c) => {
      // Must match all selected path levels
      for (let i = 0; i < selectedPathLevels.length; i++) {
        if (c.pathLevels[i] !== selectedPathLevels[i]) return false;
      }
      return true;
    });
  }, [parsedCategories, selectedPathLevels]);

  // Auto-select the first category when the available categories change (or when initially loading)
  useEffect(() => {
    if (availableCategories.length > 0) {
      if (selectedCategory === "ALL") return; // Allow "All Categories" to stay selected

      // Find what the base category of the currently fetching selectedCategory is
      const currentSelectedBase = selectedCategory?.split(" - ").pop().trim();
      const isValidCurrent = currentSelectedBase && availableCategories.some(c => c.baseCategory === currentSelectedBase);
      
      if (!selectedCategory || !isValidCurrent) {
        setSelectedCategory(availableCategories[0].categoryName);
      }
    }
  }, [availableCategories, selectedCategory]);

  if (loading && !report) {
    return (
      <Box
        sx={{
          height: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: COLORS.PRIMARY }} />
      </Box>
    );
  }

  if (error || !report) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          }
        >
          {error || "Report not found"}
        </Alert>
      </Box>
    );
  }

  const { projectId, checklistResponseId } = report;
  const responses = checklistResponseId?.responses || [];

  // Calculate stats for THIS report
  const stats = { yes: 0, no: 0, na: 0, critical: 0 };

  const calculateDynamicCounts = (resp) => {
    let y = 0;
    let n = 0;
    let isCritical = !!resp.critical;
    let currentNoStreak = 0;

    // Check history
    if (resp.history && Array.isArray(resp.history) && resp.history.length > 0) {
      resp.history.forEach((h) => {
        const hAns = (h.answer || "").toUpperCase();
        if (hAns === "YES") {
          y++;
          currentNoStreak = 0;
        } else if (hAns === "NO") {
          n++;
          currentNoStreak++;
          if (currentNoStreak >= 3) {
            isCritical = true;
          }
        } else {
          currentNoStreak = 0;
        }
      });
    } else {
      // Fallback to current answer if history is empty
      const currentAns = (resp.answer || "").toUpperCase();
      if (currentAns === "YES") {
        y++;
      } else if (currentAns === "NO") {
        n++;
      }
    }
    
    return { y, n, isCritical };
  };

  const processResponsesForStats = (resps) => {
    resps.forEach((resp) => {
      const counts = calculateDynamicCounts(resp);
      stats.yes += counts.y;
      stats.no += counts.n;

      const ans = (resp.answer || "").toUpperCase();
      if (ans === "NA") stats.na++;

      // Check if question was marked critical in the response or history
      if (counts.isCritical) stats.critical++;
    });
  };

  // Process either categorized or flat responses to avoid double counting
  const categorizedIssues = checklistResponseId?.categorizedIssues || [];
  const categoryStats = [];

  if (categorizedIssues.length > 0) {
    categorizedIssues.forEach(cat => {
      let cStats = { yesCount: 0, noCount: 0, criticalCount: 0, closedCount: 0 };
      if (cat.responses) {
        cat.responses.forEach(resp => {
           const counts = calculateDynamicCounts(resp);
           cStats.yesCount += counts.y;
           cStats.noCount += counts.n;
           if (counts.isCritical) cStats.criticalCount++;
           const ans = (resp.answer || "").toUpperCase();
           if (ans === "YES") cStats.closedCount++;
        });
        processResponsesForStats(cat.responses);
      }
      categoryStats.push({ categoryName: cat.categoryName, ...cStats });
    });
  } else {
    processResponsesForStats(responses);
  }

  const getStatusChip = (status) => {
    const statusMap = {
      CLOSED: { color: "#E8F5E9", textColor: "#2E7D32" },
      SUBMITTED: { color: "#E3F2FD", textColor: "#1976D2" },
      PENDING: { color: "#FFF3E0", textColor: "#EF6C00" },
      CRITICAL: { color: "#FFEBEE", textColor: "#D32F2F" },
    };
    const config = statusMap[status] || {
      color: "#F5F5F5",
      textColor: "#757575",
    };
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: config.textColor,
          fontWeight: 700,
          fontSize: "11px",
          borderRadius: "6px",
        }}
      />
    );
  };

  const getAnswerChip = (answer) => {
    const config = {
      YES: { color: "#E8F5E9", textColor: "#2E7D32" },
      NO: { color: "#FFEBEE", textColor: "#D32F2F" },
      NA: { color: "#F5F5F5", textColor: "#757575" },
    }[answer] || { color: "#F5F5F5", textColor: "#757575" };

    return (
      <Chip
        label={answer}
        size="small"
        sx={{
          backgroundColor: config.color,
          color: config.textColor,
          fontWeight: 800,
          px: 1,
          borderRadius: "4px",
        }}
      />
    );
  };

  const getAllImages = (resp) => {
    const list = [];
    const seenUrls = new Set();

    const addImage = (urlPath, type, respondedAt, remark, solutionRemark, isSolutionImage) => {
      if (!urlPath || typeof urlPath !== "string" || urlPath.trim() === "") return;
      const cleanUrl = urlPath.trim();
      if (seenUrls.has(cleanUrl)) return;
      seenUrls.add(cleanUrl);
      list.push({
        url: cleanUrl,
        type,
        respondedAt: respondedAt || resp.respondedAt,
        remark: remark || (isSolutionImage ? "Solution provided" : "Adding remark"),
        solutionRemark,
        isSolutionImage
      });
    };

    // 1. Traverse history
    if (resp.history && resp.history.length > 0) {
      resp.history.forEach((h) => {
        addImage(h.image, "Observation", h.respondedAt, h.imageRemark || h.generalRemark, h.imageSolutionRemark, false);
        addImage(h.imageSolution, "Solution", h.respondedAt, h.imageSolutionRemark, null, true);
      });
    }

    // 2. Fallback/direct fields
    addImage(resp.image, "Observation", resp.respondedAt, resp.imageRemark || resp.generalRemark, resp.imageSolutionRemark, false);
    addImage(resp.imageSolution, "Solution", resp.respondedAt, resp.imageSolutionRemark, null, true);

    // 3. Also check resp.photos array
    if (resp.photos && resp.photos.length > 0) {
      resp.photos.forEach((p) => {
        addImage(p.url, p.remark ? "Photo" : "Observation", p.uploadedAt, p.remark, null, false);
      });
    }

    return list;
  };

  const handleDownloadSubmit = async () => {
    try {
      setIsDownloading(true);
      const queryParams = new URLSearchParams({ type: downloadType });
      if (downloadCategories.length > 0 && !downloadCategories.includes("ALL")) {
        queryParams.append("issueCategories", downloadCategories.join(","));
      }

      const response = await axios.get(
        `${url}/api/field-inspection-reports/${report._id}/download?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `FIR_Report_${report.reportCounter || report._id.substring(report._id.length - 6)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      setDownloadModalOpen(false);
    } catch (err) {
      console.error("Download failed", err);
      // Optional: show error toast here
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: "1400px", mx: "auto" }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: COLORS.SECONDARY, mr: 1 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: COLORS.SECONDARY,
                fontSize: "24px",
                fontFamily: FONT_FAMILY,
              }}
            >
              Detailed Inspection Report
            </Typography>
            <Typography
              sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: "14px", mt: 0.5 }}
            >
              Report ID:{" "}
              {report.reportCounter ||
                report._id.substring(report._id.length - 6).toUpperCase()}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={isDownloading ? <CircularProgress size={18} color="inherit" /> : <Icon name="download1" size={18} />}
            onClick={() => setDownloadModalOpen(true)}
            disabled={isDownloading}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              borderColor: COLORS.SECONDARY,
              color: COLORS.SECONDARY,
              "&:hover": {
                borderColor: COLORS.PRIMARY,
                backgroundColor: `${COLORS.SECONDARY}08`,
              },
            }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Info Cards */}
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: "16px",
              height: "100%",
              border: "1px solid #eee",
            }}
            elevation={0}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Icon name="Document" color={COLORS.PRIMARY} size={20} />
              <Typography sx={{ fontWeight: 700, color: COLORS.PRIMARY }}>
                Project Details
              </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: COLORS.LIGHT_SECONDARY,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mb: 0.5,
                }}
              >
                Project Name
              </Typography>
              <Typography sx={{ fontWeight: 600, color: COLORS.TEXT }}>
                {projectId?.projectName || "-"}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: COLORS.LIGHT_SECONDARY,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mb: 0.5,
                }}
              >
                Developer
              </Typography>
              <Typography sx={{ fontWeight: 500, color: COLORS.TEXT }}>
                {projectId?.companyName || projectId?.clientName || "-"}
              </Typography>
            </Box>
            {projectId?.projectType && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: COLORS.LIGHT_SECONDARY,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    mb: 0.5,
                  }}
                >
                  Category
                </Typography>
                <Chip
                  label={projectId.projectType}
                  size="small"
                  sx={{
                    borderRadius: "6px",
                    fontWeight: 600,
                    backgroundColor: "#f0f2f5",
                  }}
                />
              </Box>
            )}
            <Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: COLORS.LIGHT_SECONDARY,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mb: 1,
                }}
              >
                Assigned Engineers
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {projectId?.assignedEngineers?.map((eng, idx) => (
                  <Chip
                    key={idx}
                    label={typeof eng === "string" ? eng : eng.name}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: "4px", fontSize: "11px" }}
                  />
                )) || "-"}
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: "16px",
              height: "100%",
              border: "1px solid #eee",
            }}
            elevation={0}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Icon name="calendar" color={COLORS.PRIMARY} size={20} />
              <Typography sx={{ fontWeight: 700, color: COLORS.PRIMARY }}>
                Inspection Meta
              </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: COLORS.LIGHT_SECONDARY,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mb: 0.5,
                }}
              >
                Report Date & Time
              </Typography>
              <Typography sx={{ fontWeight: 600, color: COLORS.TEXT }}>
                {formatDate(report.reportDate)} at{" "}
                {formatTime(report.createdAt)}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: COLORS.LIGHT_SECONDARY,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mb: 0.5,
                }}
              >
                Engineer
              </Typography>
              <Typography sx={{ fontWeight: 500, color: COLORS.TEXT }}>
                {report.engineerId?.name || "-"}
              </Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  color: COLORS.LIGHT_SECONDARY,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mb: 0.5,
                }}
              >
                Status
              </Typography>
              {getStatusChip(report.status)}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: "16px",
              height: "100%",
              border: "1px solid #eee",
            }}
            elevation={0}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Icon name="sitemap" color={COLORS.PRIMARY} size={20} />
              <Typography sx={{ fontWeight: 700, color: COLORS.PRIMARY }}>
                Location/Section Path
              </Typography>
            </Box>
            <Divider sx={{ mb: 2.5 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {(report.nodePathExtraDetails || report.nodePath || "")
                .split(/ \/ | - /)
                .map((part, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      pl: idx * 2.5,
                    }}
                  >
                    {idx > 0 && (
                      <Icon
                        name="downArrow"
                        size={14}
                        color={COLORS.LIGHT_SECONDARY}
                        sx={{ mt: 0.3 }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontWeight: idx === 0 ? 700 : 500,
                        color: idx === 0 ? COLORS.SECONDARY : COLORS.TEXT,
                        fontSize: idx === 0 ? "14px" : "13px",
                      }}
                    >
                      {part}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sx={{ width: "100%" }}>
          <Paper
            sx={{ p: 3, borderRadius: "16px", border: "1px solid #eee", width: "100%" }}
            elevation={0}
          >
            <Box sx={{ maxWidth: "600px", margin: "0 auto" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mb: 3 }}
              >
                <Icon name="chart" color={COLORS.PRIMARY} size={20} />
                <Typography sx={{ fontWeight: 700, color: COLORS.PRIMARY }}>
                  Report Statistics
                </Typography>
              </Box>
              <Grid container spacing={3} justifyContent="center" alignItems="stretch" sx={{ width: "100%" }}>
                {[
                {
                  label: "YES Responses",
                  value: stats.yes,
                  color: "#E8F5E9",
                  textColor: "#2E7D32",
                },
                {
                  label: "NO Responses",
                  value: stats.no,
                  color: "#FFEBEE",
                  textColor: "#D32F2F",
                },
                {
                  label: "Critical Items",
                  value: stats.critical,
                  color: "#FFFFF0",
                  textColor: "#B7791F",
                },
                {
                  label: "NA / Pending",
                  value: stats.na,
                  color: "#F7FAFC",
                  textColor: "#2B6CB0",
                },
              ].map((item, idx) => (
                <Grid item xs={6} sm={3} key={idx}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      textAlign: "center",
                      backgroundColor: item.color,
                      border: `1px solid ${item.textColor}20`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: item.textColor,
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "24px",
                        fontWeight: 800,
                        color: item.textColor,
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            </Box>

            {/* Categorized Summary */}
            {(() => {
                const categoryStatsMap = {};
                if (categoryStats) {
                    categoryStats.forEach(cat => {
                        // Extract base category name if it has a path prefix
                        const baseName = cat.categoryName.includes(" - ") 
                          ? cat.categoryName.split(" - ").pop().trim() 
                          : cat.categoryName;
                          
                        if (!categoryStatsMap[baseName]) {
                          categoryStatsMap[baseName] = { 
                            categoryName: baseName, 
                            yesCount: 0, 
                            noCount: 0, 
                            criticalCount: 0, 
                            closedCount: 0 
                          };
                        }
                        
                        categoryStatsMap[baseName].yesCount += (cat.yesCount || 0);
                        categoryStatsMap[baseName].noCount += (cat.noCount || 0);
                        categoryStatsMap[baseName].criticalCount += (cat.criticalCount || 0);
                        categoryStatsMap[baseName].closedCount += (cat.closedCount || 0);
                    });
                }
                
                const mergedCategories = globalCategories.length > 0 
                    ? globalCategories.map(name => categoryStatsMap[name] || { categoryName: name, yesCount: 0, noCount: 0, criticalCount: 0, closedCount: 0 })
                    : Object.values(categoryStatsMap);
                    
                if (mergedCategories.length === 0) return null;

                const allCategories = [...mergedCategories].sort((a, b) => {
                    const aActive = ((a.yesCount||0)>0 || (a.noCount||0)>0 || (a.criticalCount||0)>0 || (a.closedCount||0)>0) ? 1 : 0;
                    const bActive = ((b.yesCount||0)>0 || (b.noCount||0)>0 || (b.criticalCount||0)>0 || (b.closedCount||0)>0) ? 1 : 0;
                    return bActive - aActive;
                });
                
                const displayCategories = allCategories.slice(0, 4);
                const remainingCount = allCategories.length - 4;
                
                return (
                <Box sx={{ mt: 4 }}>
                    <Box sx={{ 
                        border: "1px solid #E5E7EB", 
                        borderRadius: "16px",
                        overflow: "hidden",
                        maxWidth: "600px",
                        margin: "0 auto",
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                    }}>
                        <Box sx={{ 
                            display: "flex", 
                            justifyContent: "center", 
                            alignItems: "center",
                            p: 2.5,
                            pb: 2,
                            position: "relative"
                        }}>
                            <Typography sx={{ fontSize: "18px", color: COLORS.SECONDARY, fontWeight: 700, textAlign: "center" }}>
                                Issue Category Summary
                            </Typography>
                            <IconButton size="small" onClick={() => setCategoriesModalOpen(true)} sx={{ color: COLORS.SECONDARY, p: 0.5, position: "absolute", right: 16 }}>
                                <Icon name="info" size={20} />
                            </IconButton>
                        </Box>
                        
                        <TableContainer sx={{ px: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>Issue Category</TableCell>
                                        <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>Yes</TableCell>
                                        <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>No</TableCell>
                                        <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>Critical</TableCell>
                                        <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 600, borderBottom: "2px solid #E5E7EB", py: 1.5 }}>Closed</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayCategories.map((cat, idx) => (
                                        <TableRow key={idx} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                            <TableCell sx={{ color: COLORS.SECONDARY, fontWeight: 600, fontSize: "13px", py: 2 }}>{cat.categoryName}</TableCell>
                                            <TableCell align="center" sx={{ color: "#38A169", fontWeight: 600, py: 2 }}>{cat.yesCount || 0}</TableCell>
                                            <TableCell align="center" sx={{ color: "#E53E3E", fontWeight: 600, py: 2 }}>{cat.noCount || 0}</TableCell>
                                            <TableCell align="center" sx={{ color: "#D69E2E", fontWeight: 600, py: 2 }}>{cat.criticalCount || 0}</TableCell>
                                            <TableCell align="center" sx={{ color: COLORS.LIGHT_SECONDARY, fontWeight: 600, py: 2 }}>{cat.closedCount || 0}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        {remainingCount > 0 && (
                            <Box 
                                sx={{ 
                                    textAlign: "center", 
                                    py: 2, 
                                    cursor: "pointer",
                                    color: COLORS.LIGHT_SECONDARY,
                                    "&:hover": { backgroundColor: "#F9FAFB" }
                                }}
                                onClick={() => setCategoriesModalOpen(true)}
                            >
                                <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                                    +{remainingCount} more categories
                                </Typography>
                            </Box>
                        )}
                        {remainingCount <= 0 && <Box sx={{ pb: 1 }} />}
                    </Box>
                </Box>
                );
            })()}
          </Paper>
        </Grid>

        {/* Summary / Remarks Section */}
        {report.summary && (
          <Grid item xs={12}>
            <Paper
              sx={{ p: 3, borderRadius: "16px", border: "1px solid #eee" }}
              elevation={0}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: COLORS.PRIMARY,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Icon name="Action2" size={18} />
                Executive Summary / Remarks
              </Typography>
              <Typography
                sx={{
                  color: COLORS.TEXT,
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}
              >
                {report.summary}
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* Inspection Checklist Table */}

        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box sx={{ mt: 4, mb: 2, textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: COLORS.SECONDARY, mb: 1 }}
            >
              Inspection Checklist
            </Typography>
            <Typography
              sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: "14px" }}
            >
              Detailed responses for each inspection point
            </Typography>
          </Box>

          <Paper
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              border: "1px solid #eee",
              mb: 4,
              width: "100%",
              maxWidth: "1200px",
            }}
            elevation={0}
          >
            {/* Category Path Selectors & Tabs */}
            {report.categoryIssues && report.categoryIssues.length > 0 && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fff' }}>
                {maxDepth > 0 && (
                  <Box sx={{ p: 2, pt: 2.5, display: "flex", flexDirection: "column", gap: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eee" }}>
                    {levelOptions.map((options, levelIndex) => {
                      if (options.length === 0) return null;
                      return (
                        <Box 
                          key={levelIndex} 
                          sx={{ 
                            display: "flex", 
                            gap: 1.5, 
                            overflowX: "auto", 
                            pb: 0.5, 
                            "&::-webkit-scrollbar": { display: "none" } 
                          }}
                        >
                          {options.map((opt) => {
                            const isSelected = selectedPathLevels[levelIndex] === opt;
                            return (
                              <Button
                                key={opt}
                                variant={isSelected ? "contained" : "outlined"}
                                onClick={() => {
                                  const newLevels = [...selectedPathLevels];
                                  newLevels[levelIndex] = opt;
                                  setSelectedPathLevels(newLevels.slice(0, levelIndex + 1));
                                }}
                                endIcon={levelIndex === 0 ? <Icon name="downArrow" size={10} color={isSelected ? "#fff" : COLORS.SECONDARY} /> : null}
                                sx={{
                                  borderRadius: "20px",
                                  textTransform: "none",
                                  minWidth: "auto",
                                  whiteSpace: "nowrap",
                                  px: 2.5,
                                  py: 0.6,
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  backgroundColor: isSelected ? COLORS.SECONDARY : "#fff",
                                  color: isSelected ? "#fff" : COLORS.SECONDARY,
                                  borderColor: isSelected ? COLORS.SECONDARY : "#e5e7eb",
                                  boxShadow: "none",
                                  "&:hover": {
                                    backgroundColor: isSelected ? COLORS.SECONDARY : "#f9fafb",
                                    borderColor: COLORS.SECONDARY,
                                    boxShadow: "none",
                                  },
                                  "& .MuiButton-endIcon": {
                                    ml: 1,
                                    opacity: 0.8
                                  }
                                }}
                              >
                                {opt}
                              </Button>
                            );
                          })}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                <Tabs
                  value={
                    selectedCategory === "ALL" 
                      ? "ALL" 
                      : (selectedCategory || availableCategories[0]?.categoryName || false)
                  }
                  onChange={(e, newValue) => setSelectedCategory(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    px: 2,
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '14px',
                      minHeight: '48px',
                      color: COLORS.LIGHT_SECONDARY,
                      '&.Mui-selected': {
                        color: COLORS.PRIMARY,
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: COLORS.PRIMARY,
                      height: '3px',
                      borderTopLeftRadius: '3px',
                      borderTopRightRadius: '3px',
                    }
                  }}
                >
                  <Tab label="All Categories" value="ALL" />
                  {availableCategories.map((cat, idx) => (
                    <Tab 
                      key={idx} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {cat.baseCategory}
                          <Chip 
                            label={cat.yesCount + cat.noCount + cat.naCount} 
                            size="small" 
                            sx={{ height: '20px', fontSize: '11px', fontWeight: 700, bgcolor: '#f0f2f5' }}
                          />
                        </Box>
                      } 
                      value={cat.categoryName} 
                    />
                  ))}
                </Tabs>
              </Box>
            )}

            {/* Table Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "70px 1.5fr 120px 120px 2fr",
                gap: 2,
                px: 3,
                py: 2.5,
                backgroundColor: "#f8fafc",
                borderBottom: "2px solid #eee",
              }}
            >
              {[
                "Sr.No.",
                "Question/CheckList",
                "Yes / No",
                "Response",
                "Remarks History",
              ].map((h, i) => (
                <Typography
                  key={i}
                  sx={{
                    fontWeight: 700,
                    color: COLORS.SECONDARY,
                    fontSize: "13px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    textAlign: i >= 2 && i <= 3 ? "center" : "left",
                  }}
                >
                  {h}
                </Typography>
              ))}
            </Box>

            {loading ? (
              <Box sx={{ p: 6, textAlign: "center" }}>
                <CircularProgress sx={{ color: COLORS.PRIMARY }} />
                <Typography sx={{ mt: 2, color: COLORS.LIGHT_SECONDARY, fontWeight: 500 }}>
                  Loading category responses...
                </Typography>
              </Box>
            ) : responses.length === 0 ? (
              <Box sx={{ p: 6, textAlign: "center" }}>
                <Icon
                  name="Document"
                  size={48}
                  color={COLORS.LIGHT_SECONDARY}
                />
                <Typography
                  sx={{ mt: 2, color: COLORS.LIGHT_SECONDARY, fontWeight: 500 }}
                >
                  No checks added for this category.
                </Typography>
              </Box>
            ) : (
              responses.map((resp, index) => {
                const latest = resp.history?.[resp.history.length - 1] || {};
                return (
                  <Box
                    key={index}
                    sx={{
                      borderBottom:
                        index === responses.length - 1
                          ? "none"
                          : "1px solid #f0f0f0",
                    }}
                  >
                    {/* Question Row */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "70px 1.5fr 120px 120px 2fr",
                        gap: 2,
                        px: 3,
                        py: 3,
                        backgroundColor: "#fff",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Sr No */}
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: COLORS.TEXT,
                          fontSize: "15px",
                        }}
                      >
                        {(resp.srNo || index + 1).toString().padStart(2, "0")}.
                      </Typography>

                      {/* Question */}
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: COLORS.TEXT,
                            fontSize: "15px",
                            mb: 0.8,
                            lineHeight: 1.4,
                          }}
                        >
                          {resp.question}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: COLORS.LIGHT_SECONDARY,
                          }}
                        >
                          Asked by:{" "}
                          {resp.addedBy?.name || "Test - Durva Engineer"}
                        </Typography>
                      </Box>

                      {/* Yes / No Counter */}
                      <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#2E7D32",
                            fontSize: "15px",
                          }}
                        >
                          Y: {calculateDynamicCounts(resp).y}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: "#D32F2F",
                            fontSize: "15px",
                          }}
                        >
                          N: {calculateDynamicCounts(resp).n}
                        </Typography>
                      </Box>

                      {/* Response */}
                      <Typography
                        sx={{
                          fontWeight: 800,
                          color:
                            resp.answer === "YES"
                              ? "#2E7D32"
                              : resp.answer === "NO"
                                ? "#D32F2F"
                                : COLORS.LIGHT_SECONDARY,
                          fontSize: "15px",
                          textAlign: "center",
                        }}
                      >
                        {resp.answer || "NA"}
                      </Typography>

                      {/* Remark Column History */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {(() => {
                          const renderRemarkEntry = (entry, isLast) => (
                            <Box key={entry.id || Math.random()} sx={{ borderBottom: isLast ? 'none' : '1px dashed #eee', pb: isLast ? 0 : 1.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 700,
                                    color: COLORS.TEXT,
                                  }}
                                >
                                  {formatDate(entry.respondedAt).split(" ").slice(0, 2).join(" ")}:
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 800,
                                    color:
                                      entry.answer === "YES"
                                        ? "#2E7D32"
                                        : entry.answer === "NO"
                                          ? "#D32F2F"
                                          : COLORS.LIGHT_SECONDARY,
                                  }}
                                >
                                  {entry.answer}
                                </Typography>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: COLORS.TEXT,
                                  mb: 1,
                                  lineHeight: 1.5,
                                }}
                              >
                                {entry.generalRemark || "No remark provided"}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  color: COLORS.LIGHT_SECONDARY,
                                  fontStyle: "italic",
                                  borderTop: "1px dashed #f5f5f5",
                                  pt: 0.5,
                                }}
                              >
                                Responded by: {entry.respondedBy?.name || entry.addedBy?.name || report.engineerId?.name || "Unknown"}
                              </Typography>
                            </Box>
                          );

                          if (resp.history && resp.history.length > 0) {
                            // Render all history entries
                            return resp.history.map((h, hIdx) => renderRemarkEntry(h, hIdx === resp.history.length - 1));
                          } else {
                            // Fallback to current resp if no history
                            return renderRemarkEntry(resp, true);
                          }
                        })()}
                      </Box>
                    </Box>

                    {/* Media Sub-Row */}
                    {(() => {
                      const allImages = getAllImages(resp);
                      if (allImages.length === 0) return null;
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            alignItems: "stretch",
                            gap: 3,
                            px: 3,
                            pb: 4,
                            backgroundColor: "#fff",
                            width: "100%",
                          }}
                        >
                          {allImages.map((imgData, imgIdx) => (
                            <Box
                              key={imgIdx}
                              sx={{
                                backgroundColor: "#fff",
                                width: {
                                  xs: "100%",
                                  sm: "calc(50% - 12px)",
                                  md: "calc(33.333% - 16px)",
                                },
                                maxWidth: "400px",
                                minWidth: "280px",
                                height: "auto",
                                display: "flex",
                              }}
                            >
                              <Box
                                sx={{
                                  width: "100%",
                                  backgroundColor: "#fff",
                                  p: 1.5,
                                  borderRadius: "16px",
                                  border: "1px solid #eee",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                  display: "flex",
                                  flexDirection: "column",
                                  mx: "auto",
                                }}
                              >
                                <Box
                                  sx={{
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    borderRadius: "12px",
                                    mb: 2,
                                    border: "1px solid #f0f0f0",
                                    aspectRatio: "1/1",
                                    width: "100%",
                                  }}
                                >
                                  <img
                                    src={`${url}${imgData.url}`}
                                    alt={imgData.type}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      display: "block",
                                    }}
                                    onClick={() => setSelectedImage(`${url}${imgData.url}`)}
                                  />
                                </Box>
                                <Box sx={{ px: 1, flexGrow: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: "11px",
                                      color: COLORS.LIGHT_SECONDARY,
                                      mb: 1.5,
                                    }}
                                  >
                                    {imgData.type} -{" "}
                                    {formatDate(imgData.respondedAt)},{" "}
                                    {formatTime(imgData.respondedAt)}
                                  </Typography>
                                  <Typography sx={{ fontSize: "14px", mb: 1 }}>
                                    <span
                                      style={{
                                        fontWeight: 700,
                                        color: imgData.isSolutionImage ? "#2E7D32" : COLORS.SECONDARY,
                                      }}
                                    >
                                      {imgData.isSolutionImage ? "Solution:" : "Remark:"}
                                    </span>{" "}
                                    {imgData.remark}
                                  </Typography>
                                  {!imgData.isSolutionImage && imgData.solutionRemark && (
                                    <Typography
                                      sx={{ fontSize: "14px", color: "#2E7D32" }}
                                    >
                                      <span style={{ fontWeight: 700 }}>
                                        Solution:
                                      </span>{" "}
                                      {imgData.solutionRemark}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}
                  </Box>
                );
              })
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Image Preview Modal */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={() => setSelectedImage(null)}
            sx={{
              position: "absolute",
              top: 15,
              right: 15,
              backgroundColor: "#fff",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              color: COLORS.REJECTED || "#FF6961",
              "&:hover": {
                backgroundColor: COLORS.REJECTED || "#FF6961",
                color: "#fff",
              },
              zIndex: 10,
            }}
          >
            <Icon name="close2" size={20} stroke={2.5} />
          </IconButton>
          <img
            src={selectedImage}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "12px",
              border: "4px solid #fff",
            }}
          />
        </Box>
      </Dialog>

      {/* Categories Modal */}
      <Dialog 
          open={categoriesModalOpen} 
          onClose={() => setCategoriesModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
              sx: { borderRadius: "16px", p: 1 }
          }}
      >
          <DialogTitle sx={{ 
              fontFamily: FONT_FAMILY, 
              fontWeight: 700, 
              color: COLORS.SECONDARY,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1
          }}>
              Issue Category Summary
              <IconButton size="small" onClick={() => setCategoriesModalOpen(false)}>
                  <Icon name="close2" size={20} />
              </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, borderTop: "none" }}>
              {(() => {
                  const categoryStatsMap = {};
                  if (categoryStats) {
                      categoryStats.forEach(cat => categoryStatsMap[cat.categoryName] = cat);
                  }
                  
                  const mergedCategories = globalCategories.length > 0 
                      ? globalCategories.map(name => categoryStatsMap[name] || { categoryName: name, yesCount: 0, noCount: 0, criticalCount: 0, closedCount: 0 })
                      : categoryStats || [];
                  
                  const allCategories = [...mergedCategories].sort((a, b) => {
                      const aActive = ((a.yesCount||0)>0 || (a.noCount||0)>0 || (a.criticalCount||0)>0 || (a.closedCount||0)>0) ? 1 : 0;
                      const bActive = ((b.yesCount||0)>0 || (b.noCount||0)>0 || (b.criticalCount||0)>0 || (b.closedCount||0)>0) ? 1 : 0;
                      return bActive - aActive;
                  });

                  return (
                      <>
                          <Box sx={{ px: 2, pb: 1 }}>
                              <Typography sx={{ fontSize: "13px", color: COLORS.LIGHT_SECONDARY, mb: 1 }}>
                                  Showing {allCategories.length} categories
                              </Typography>
                          </Box>
                          <TableContainer>
                              <Table stickyHeader size="small">
                                  <TableHead>
                                      <TableRow>
                                          <TableCell sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>Issue Category</TableCell>
                                          <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>Yes</TableCell>
                                          <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>No</TableCell>
                                          <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>Critical</TableCell>
                                          <TableCell align="center" sx={{ color: COLORS.SECONDARY, fontWeight: 700, backgroundColor: "#F9FAFB" }}>Closed</TableCell>
                                      </TableRow>
                                  </TableHead>
                                  <TableBody>
                                      {allCategories.map((cat, idx) => (
                                  <TableRow key={idx}>
                                      <TableCell sx={{ color: COLORS.SECONDARY, fontWeight: 600, fontSize: "13px" }}>{cat.categoryName}</TableCell>
                                      <TableCell align="center" sx={{ color: "#38A169", fontWeight: 600 }}>{cat.yesCount || 0}</TableCell>
                                      <TableCell align="center" sx={{ color: "#E53E3E", fontWeight: 600 }}>{cat.noCount || 0}</TableCell>
                                      <TableCell align="center" sx={{ color: "#D69E2E", fontWeight: 600 }}>{cat.criticalCount || 0}</TableCell>
                                      <TableCell align="center" sx={{ color: COLORS.LIGHT_SECONDARY, fontWeight: 600 }}>{cat.closedCount || 0}</TableCell>
                                  </TableRow>
                                      ))}
                                  </TableBody>
                              </Table>
                          </TableContainer>
                      </>
                  );
              })()}
          </DialogContent>
      </Dialog>

      {/* Download Modal */}
      <Dialog
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "16px", p: 1 }
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: FONT_FAMILY, 
          fontWeight: 700, 
          color: COLORS.SECONDARY,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1
        }}>
          Download PDF Report
          <IconButton size="small" onClick={() => setDownloadModalOpen(false)}>
            <Icon name="close2" size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3, borderTop: "none" }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, color: COLORS.SECONDARY, mb: 1 }}>Question Type</Typography>
            <FormControl component="fieldset">
              <RadioGroup row value={downloadType} onChange={(e) => setDownloadType(e.target.value)}>
                <FormControlLabel value="all" control={<Radio sx={{ color: COLORS.PRIMARY, '&.Mui-checked': { color: COLORS.PRIMARY } }} />} label="All" />
                <FormControlLabel value="no" control={<Radio sx={{ color: COLORS.PRIMARY, '&.Mui-checked': { color: COLORS.PRIMARY } }} />} label="No" />
                <FormControlLabel value="resolved" control={<Radio sx={{ color: COLORS.PRIMARY, '&.Mui-checked': { color: COLORS.PRIMARY } }} />} label="No to Yes (Resolved)" />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, color: COLORS.SECONDARY, mb: 1 }}>Categories</Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={downloadCategories.includes("ALL")}
                    onChange={(e) => {
                      if (e.target.checked) setDownloadCategories(["ALL"]);
                    }}
                    sx={{ color: COLORS.PRIMARY, '&.Mui-checked': { color: COLORS.PRIMARY } }}
                  />
                }
                label="All Categories"
              />
              {globalCategories.map(cat => (
                <FormControlLabel
                  key={cat}
                  control={
                    <Checkbox
                      checked={!downloadCategories.includes("ALL") && downloadCategories.includes(cat)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setDownloadCategories(prev => prev.includes("ALL") ? [cat] : [...prev, cat]);
                        } else {
                          const newSelection = downloadCategories.filter(c => c !== cat);
                          setDownloadCategories(newSelection.length === 0 ? ["ALL"] : newSelection);
                        }
                      }}
                      sx={{ color: COLORS.PRIMARY, '&.Mui-checked': { color: COLORS.PRIMARY } }}
                    />
                  }
                  label={cat}
                />
              ))}
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={() => setDownloadModalOpen(false)} 
            sx={{ color: COLORS.LIGHT_SECONDARY, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadSubmit} 
            disabled={isDownloading}
            variant="contained"
            sx={{ 
              backgroundColor: COLORS.PRIMARY, 
              color: "#fff", 
              textTransform: 'none', 
              fontWeight: 600, 
              borderRadius: "8px",
              px: 3,
              '&:hover': { backgroundColor: COLORS.PRIMARY }
            }}
            startIcon={isDownloading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isDownloading ? "Downloading..." : "Download Report"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportDetailView;
